/**
 * Render a base config diff for the terminal.
 *
 * Entity ids come off the server, so every one is sanitized before it reaches
 * stdout — the same posture `bases promote` takes with its advisory messages.
 */

import { sanitizeTerminalText } from '../../lib/terminal-output.js';
import { ENTITY_KINDS, type ConfigDiff, type DiffBucket, type EntityKind } from './types.js';

const LABELS: Record<EntityKind, string> = {
  record_types: 'record type',
  relationship_types: 'relationship type',
  inbound_webhooks: 'inbound webhook',
  triggers: 'trigger',
  toolsets: 'toolset',
  actions: 'action',
  seeds: 'seed',
};

function bucket(diff: ConfigDiff, kind: EntityKind): DiffBucket<{ id: string }> | undefined {
  return diff[kind] as DiffBucket<{ id: string }> | undefined;
}

function isEmptyBucket(b: DiffBucket<{ id: string }> | undefined): boolean {
  return !b || (b.added.length === 0 && b.modified.length === 0 && b.removed.length === 0);
}

export function isDiffEmpty(diff: ConfigDiff): boolean {
  return ENTITY_KINDS.every((kind) => isEmptyBucket(bucket(diff, kind)));
}

export interface Removal {
  kind: EntityKind;
  id: string;
}

/** Every entity present on the base but absent locally — the candidates for `--prune`. */
export function collectRemovals(diff: ConfigDiff): Removal[] {
  const out: Removal[] = [];
  for (const kind of ENTITY_KINDS) {
    for (const entity of bucket(diff, kind)?.removed ?? []) out.push({ kind, id: entity.id });
  }
  return out;
}

export function countChanges(diff: ConfigDiff): {
  added: number;
  modified: number;
  removed: number;
} {
  let added = 0;
  let modified = 0;
  let removed = 0;
  for (const kind of ENTITY_KINDS) {
    const b = bucket(diff, kind);
    if (!b) continue;
    added += b.added.length;
    modified += b.modified.length;
    removed += b.removed.length;
  }
  return { added, modified, removed };
}

/** Shared with `sync.ts`, so the base surface has ONE spelling of each color. */
export const GREEN = '\x1b[32m';
export const YELLOW = '\x1b[33m';
export const RED = '\x1b[31m';
export const BOLD = '\x1b[1m';
export const RESET = '\x1b[0m';

/**
 * Green `+` added, yellow `~` modified, red `-` removed. Removals are the
 * desired-state foot-gun — the config is the full desired state, so deleting a
 * local file reads as a removal — so they close with the note that `--prune` is
 * what actually applies them.
 */
export function renderDiff(diff: ConfigDiff): string {
  const lines: string[] = [];
  for (const kind of ENTITY_KINDS) {
    const b = bucket(diff, kind);
    if (!b || isEmptyBucket(b)) continue;
    lines.push(`${BOLD}${LABELS[kind]}s${RESET}`);
    for (const e of b.added) lines.push(`${GREEN}  + ${sanitizeTerminalText(e.id)}${RESET}`);
    for (const m of b.modified) lines.push(`${YELLOW}  ~ ${sanitizeTerminalText(m.after.id)}${RESET}`);
    for (const e of b.removed) lines.push(`${RED}  - ${sanitizeTerminalText(e.id)}${RESET}`);
  }
  const { added, modified, removed } = countChanges(diff);
  lines.push('');
  lines.push(
    `${GREEN}${added} added${RESET}, ${YELLOW}${modified} modified${RESET}, ${RED}${removed} removed${RESET}`,
  );
  if (removed > 0) {
    lines.push(
      `${RED}  ${removed} entit${removed === 1 ? 'y' : 'ies'} exist on the base but not in your files — re-run with --prune to delete (record types cascade to their records).${RESET}`,
    );
  }
  return lines.join('\n');
}
